import {
    ConceptType,
    ObjectStatus,
    AttributeType,
    ParkingStatus,
    ParkingStock,
    ResidenceType,
    StageStatus,
    RentalStatus,
    PremiseStatus
} from "features/search/filters/enums";
import {ObjectImageType} from "features/template/viewModels";

export interface ApiFilters {
    siteId?:                   string[];
    areaSiteId?:               string[];
    areaAttributesToRetrieve?: string[];
    tags?:                     string[];
    concept?:                  string[];
    disposalForm?:             string[];
    inhabitDate?:              string;
    minArea?:                  number;
    maxArea?:                  number;
    minPrice?:                 number;
    minJmoyPrice?:             number;
    maxPrice?:                 number;
    maxJmoyPrice?:             number;
    minRooms?:                 number;
    maxRooms?:                 number;
    rooms?:                    number[];
    outDoorAreas?:             string[];
    stageStatus?:              string[];
    residenceTypes?:           string[];
    parkingSpaceStatus?:       string[];
    objectStatus?:             string[];
    showing?:                  string;
    project?:                  string;
    geoSearch?:                GeoSearch;
    hideAsMoveInReady?:        boolean;
    excludeStageIds?:          string[];
    includeStageIds?:          string[];
    stageId?:                  string[];
    types?:                    string[];
    elevator?:                 boolean;
    isEditMode?:               boolean;
    hasMadePurchase?:          boolean;
    activeVipPeriod?:          boolean;
}

export interface ShowingsRange {
    from: string | undefined;
    to: string | undefined;
}

export interface GeoSearch {
    lat:    number;
    lng:    number;
    radius?: number;
}

export interface SearchCount {
    objects: number;
    stages: number;
    areas: number;
}

export interface Tag {
    id: string;
    name: string;
    type: string;
    url?: string;
    uniqueName?: string;
    rank?: number;
    fiName?: string;
    seName?: string;
    noName?: string;
    parentTag?: Tag;
    parents?: string[];
    index?: number;
    disabledInSearch?: boolean;
    active?: boolean;
    activeChildren?: boolean;
    children?: Tag[];
    searchImage?: EpiImage;
    displayName?: string;
    hasRental?: boolean;
    isParent?: boolean;
    isChild?: boolean;

    area?: {
        id: string;
        name: string;
        displayName?: string;
    }

    county?: {
        id: string;
        name: string;
        displayName?: string;
    }

    municipality?: {
        id: string;
        name: string;
        displayName?: string;
    }

}

export interface EpiImage {
    url: string;
    type?: string;
    contentGuid?: string;
    orgWidth?: number;
    orgHeight?: number;
    orgSizeKb?: number;
    ratio?: number;
    tall?: boolean;
    widthBased?: boolean;
    altText?: string;
    description?: string;
    footnote?: string;
}

export interface EpiImageWithType extends EpiImage {
    type: ObjectImageType;
}

export interface NamedEntity {
    id: string;
    name: string;
    displayName?: string;
}

/**
 * Minimal shape consumed by ResidenceCard and its shared chrome
 * (getCardUrl, ShowingTag, FavoriteButton, useFavorite). SearchResult
 * satisfies this structurally; objects with a different source shape
 * (PremisesObject, RentalProjectCard) are mapped into it at the page
 * boundary. Card types that render rich content (OBJECT/PROJECT/PURCHASE)
 * narrow back to the full SearchResult via isSearchResult().
 */
export interface ResidenceCardData {
    apartmentNumber: string | undefined;
    area: NamedEntity;
    concept: ConceptType;
    displayName: string;
    floorPlan?: EpiImage;
    hasExtraShowingsInfo?: boolean;
    id: string;
    inhabitDate: Date | null;
    inhabitDateDetermined: boolean;
    inhabitDateEnd?: Date;
    maxRooms?: number;
    minRooms?: number;
    municipality: NamedEntity;
    name: string;
    objectStatus: ObjectStatus;
    searchImage: EpiImage;
    showings: Showing[];
    siteId: string;
    size?: number;
    stage: string;
    stageStatus: StageStatus;
    status?: RentalStatus | PremiseStatus;
    text?: string;
    type: string;
    types?: string[];
    url: string;
    vipStart?: number;
}

export interface SearchResult {
    address: string;
    apartmentNumber: string;
    area: NamedEntity;
    areaName: string;
    carParkingStock: ParkingStock;
    concept: ConceptType;
    county: NamedEntity;
    countyName: string;
    displayName: string;
    disposalForm: string;
    fee?: number;
    floor: number;
    floorPlan: EpiImage;
    id: string;
    images: EpiImageWithType[];
    inhabitDate: Date | null;
    inhabitDateDetermined: boolean;
    inhabitDateEnd: Date;
    inhabitDateTimeStamp: number;
    latitude: number;
    livingArea?: number;
    longitude: number;
    maxArea?: number;
    maxFee?: number;
    maxFloor: number;
    maxPrice?: number;
    maxRooms?: number;
    minArea?: number;
    minFee?: number;
    minPrice?: number;
    minRooms?: number;
    municipality: NamedEntity;
    municipalityName: string;
    name: string;
    nrOfObjects?: number;
    nrOfParkingSpots?: number;
    objectStatus: ObjectStatus;
    order?: number;
    outDoorSpaces: OutdoorArea[];
    possibleCoOwning?: boolean;
    possibleRooms: number;
    price: number;
    primaryArea?: number;
    plotArea?: number;
    otherExpenses?: number;
    publishedOnSite: string[];
    residenceType: ResidenceType;
    rooms: number;
    searchImage: EpiImage;
    secondaryArea?: number;
    shortDescription: string;
    showing: Showing;
    showings: Showing[];
    siteOrg: string;
    siteId: string;
    stage: string;
    stageName: string;
    street: string;
    project: string;
    stageStatus: StageStatus;
    keywords: string[];
    type: string;
    url: string;
    vipEnd?: number;
    vipStart?: number;

    jmnoSharedCost?: number;
    jmnoBraInternal?: number;
    jmnoFellesgjeld?: number;

    jmoyCloset?: boolean;
    jmoyOwnSauna?: boolean;
    jmoyNumberOfRoomsExtraInfo?: string;
    jmoyTotalPrice?: number;
    jmoyTotalPriceMin?: number;
    jmoyTotalPriceMax?: number;
    swanLabel?: boolean;
    parkingSpaceStatus: ParkingStatus;
    hasExtraShowingsInfo?: boolean;
    hideAsMoveInReady?: boolean;

    _geo?: {
        lat: number;
        lng: number;
    },
    _geoDistance?: number;
    successionExternalUrl?: string;
    successionPrice?: number;
    successionRealEstateAgent?: string;
}

/**
 * Runtime guard for narrowing the card's ResidenceCardData back to the full
 * SearchResult. Only objects originating from search carry `residenceType`,
 * so it discriminates real search results from mapped premises/rental data.
 */
export const isSearchResult = (
    residence: ResidenceCardData,
): residence is SearchResult => 'residenceType' in residence;

export interface Showing {
    address: string;
    city: string;
    description: string;
    startDate: string;
    endDate: string;
    startDateTimeStamp: number;
    endDateTimeStamp: number;
    id: string;
    latitude: number;
    longitude: number;
    postalCode: string;
    type: string;
    directions: string;
    stageName: string;
    url: string;
    digitalShowingUrl?: string;
}

export interface OutdoorArea {
    type: AttributeType;
    area: number;
    direction: string;
}

export interface FilteredSearch {
    [key: string]: SearchResult[],

    area: SearchResult[],
    stage: SearchResult[],
    object: SearchResult[],
}

export interface FeaturedItem {
    title: string;
    tags: Tag[];
}

export interface CategorizedSearch {
    areas: SearchResult[];
    stages: SearchResult[];
    objects: SearchResult[];
}

export interface SearchResponse<T> {
    hits:               T;
    offset:             number;
    limit:              number;
    estimatedTotalHits: number;
    processingTimeMs:   number;
    query:              string;
    facetDistribution: {
        type: {
            stage: number;
            area: number;
            object: number;
        }
    }
}

export interface TagHit {
    id:         string;
    name:       string;
    type:       string;
    uniqueName: string;
    url:        string;
    hasRental:  boolean;
    parentTag?: ParentTag;
}

export interface ParentTag {
    id:          string;
    name:        string;
    displayName: string;
    type:        string;
}

export enum InhabitDate
{
    Now,
    InSixMonths,
    InOneYear,
    InMoreThenOneYear,
}

export interface SearchQuery<T> {
    text?:                  string;
    limit?:                 number;
    offset?:                number;
    attributesToRetrieve?:  string[];
    attributesToHighlight?: string[];
    sort?:                  string[];
    attributesToCrop?:      string[];
    cropMarker?:            string;
    cropLength?:            number;
    highlightPreTag?:       string;
    highlightPostTag?:      string;

    filter: T;
}


export interface SearchFilters {
    minPrice: number;
    maxPrice: number;
    minRooms: number;
    maxRooms: number;
    rooms: string[];
    minArea: number;
    maxArea: number;
    inhabitDate: string;
    concepts: string[];
    projectStatus: string[];
    disposalForm: string[];
    attributes: string[];
    residenceType: string[];
    viewing: string;
    stageIds: string[];
    tags: Tag[];
    geoSearch?: GeoSearch;
}
